/**
 * Song Library Permanent Delete — bulk preflight aggregation helpers.
 *
 * The UI still calls the same per-track dry-run preflight endpoint (no safety
 * checks removed). Aggregation is pure; network concurrency lives in the page.
 */

import type { PermanentDeletePreflight } from "@/features/tracks/tracksApi";
import { formatEntityRef } from "@/lib/entityRef";

export type ChannelImpactChannel = NonNullable<
  NonNullable<PermanentDeletePreflight["channelImpact"]>["channels"]
>[number];

/** Bounded in-flight preflight GETs — read-only, safer than unbounded fan-out. */
export const BULK_PD_PREFLIGHT_CONCURRENCY = 6;

/** Match API tracks-permanent-delete-bulk MAX_BATCH. */
export const BULK_PD_PREFLIGHT_MAX_IDS = 50;

export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const limit = Math.max(1, Math.floor(concurrency) || 1);
  const results = new Array<R>(items.length);
  let next = 0;
  let firstError: unknown = null;

  async function runWorker(): Promise<void> {
    while (true) {
      if (firstError) return;
      const index = next;
      next += 1;
      if (index >= items.length) return;
      try {
        results[index] = await worker(items[index], index);
      } catch (err) {
        if (!firstError) firstError = err;
        return;
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => runWorker()),
  );
  if (firstError) throw firstError;
  return results;
}

export function aggregatePermanentDeletePreflights(args: {
  ids: string[];
  preflights: PermanentDeletePreflight[];
}): PermanentDeletePreflight {
  const { ids, preflights } = args;
  let jobs = 0;
  let aavs = 0;
  let playback = 0;
  let programming = 0;
  let staleCount = 0;
  const blockers: PermanentDeletePreflight["blockers"] = [];
  const stalePointers: NonNullable<PermanentDeletePreflight["stalePointers"]> =
    [];
  const channelMap = new Map<string, ChannelImpactChannel>();

  for (let i = 0; i < preflights.length; i += 1) {
    const pre = preflights[i];
    const id = ids[i] || pre.trackId;
    jobs += pre.counts.processingJobs || 0;
    aavs += pre.counts.approvedArtifactVersions || 0;
    playback += pre.counts.playbackObjects || 0;
    programming += pre.counts.programmingReferences || 0;
    staleCount += pre.counts.stalePointers || pre.stalePointers?.length || 0;
    if (pre.blockers?.length) blockers.push(...pre.blockers);
    if (pre.channelImpact?.channels?.length) {
      for (const ch of pre.channelImpact.channels) {
        const key = String(ch.channelId || ch.channelName || "unknown");
        const existing = channelMap.get(key);
        if (!existing) {
          channelMap.set(key, {
            ...ch,
            placementCount: ch.placementCount || 0,
            rotations: [...(ch.rotations || [])],
            scheduleSides: [...(ch.scheduleSides || [])],
          });
          continue;
        }
        existing.placementCount =
          (existing.placementCount || 0) + (ch.placementCount || 0);
        const sideSet = new Set(existing.scheduleSides || []);
        for (const side of ch.scheduleSides || []) sideSet.add(side);
        existing.scheduleSides = [...sideSet];
        const rotSeen = new Set(
          (existing.rotations || []).map(
            (r) => r.rotationId || r.rotationName || "",
          ),
        );
        for (const rot of ch.rotations || []) {
          const rk = rot.rotationId || rot.rotationName || "";
          if (!rk || rotSeen.has(rk)) continue;
          rotSeen.add(rk);
          existing.rotations = [...(existing.rotations || []), rot];
        }
      }
    }
    if (pre.stalePointers?.length) {
      for (const w of pre.stalePointers) {
        stalePointers.push({
          ...w,
          message: `${pre.title || formatEntityRef("track", id)}: ${w.message}`,
        });
      }
    }
  }

  const aggregatedChannels = [...channelMap.values()];
  const channelNames = aggregatedChannels
    .map((ch) => ch.channelName || ch.channelId)
    .filter((n): n is string => !!n);

  return {
    trackId: ids[0],
    title: `${ids.length} selected songs`,
    status: "archived",
    dryRun: true,
    eligible: blockers.length === 0,
    blockers,
    stalePointers,
    counts: {
      processingJobs: jobs,
      approvedArtifactVersions: aavs,
      playbackObjects: playback,
      programmingReferences: programming,
      channelCount: aggregatedChannels.length,
      stalePointers: staleCount,
    },
    channelImpact:
      programming > 0 || aggregatedChannels.length > 0
        ? {
            placementCount: programming,
            channelCount: aggregatedChannels.length,
            channels: aggregatedChannels,
            operatorMessage: `Selected songs are used across ${aggregatedChannels.length} music channel${aggregatedChannels.length === 1 ? "" : "s"}${channelNames.length ? `: ${channelNames.join(", ")}` : ""}. If you continue, these Songs will be removed from these channels.`,
            blocked: blockers.some(
              (b) =>
                String(b.code || "").includes("empty-after-removal") ||
                String(b.code || "").includes("continuity"),
            ),
            blockers,
          }
        : null,
    willRemove: [
      "Each selected Track and its complete processing lineage",
      "Associated Song Processing jobs, AAVs, and playback objects",
      "Channel Programming memberships (all schedule sides)",
      "Stale Track playback / job pointers (cleared on tombstone)",
    ],
    willRetain: [
      "Track tombstones + deletion manifests",
      "Job/AAV tombstones and append-only audit history",
      "Song History Channel Membership History freeze",
    ],
  };
}

export function estimateSerialPreflightMs(
  songCount: number,
  avgMsPerCall: number,
): number {
  return Math.max(0, songCount) * Math.max(0, avgMsPerCall);
}

export function estimateConcurrentPreflightMs(
  songCount: number,
  avgMsPerCall: number,
  concurrency: number = BULK_PD_PREFLIGHT_CONCURRENCY,
): number {
  const n = Math.max(0, songCount);
  const c = Math.max(1, concurrency);
  if (n === 0) return 0;
  return Math.ceil(n / c) * Math.max(0, avgMsPerCall);
}
